// src/lib/schema/base/breadcrumb.ts

import { validateRequiredFields, isValidUrl } from '@/lib/schema';
import { BreadcrumbList, ListItem, WithContext } from 'schema-dts';

/**
 * Interface for breadcrumb item
 */
interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Interface for e-commerce breadcrumb options
 */
interface EcommerceBreadcrumbOptions {
  baseURL: string;
  lang: string;
  currentPageName: string;
  currentPageURL: string;
  category?: string;
  categoryURL?: string;
  subcategory?: string;
  subcategoryURL?: string;
}

/**
 * Creates structured data for breadcrumbs navigation
 * @param items - Array of breadcrumb items with name and url
 * @returns JSON-LD schema for breadcrumbs or null if validation fails
 */
export function generateBreadcrumbSchema(
  items: BreadcrumbItem[]
): WithContext<BreadcrumbList> | null {
  // Validate input
  if (!items || !Array.isArray(items) || items.length === 0) {
    console.warn('Invalid breadcrumb items for schema generation');
    return null;
  }

  // Ensure all items have required properties
  const validItems = items.filter(item =>
    validateRequiredFields(item, ['name']) &&
    (item.url !== undefined)
  );

  if (validItems.length === 0) {
    console.warn('No valid breadcrumb items for schema generation');
    return null;
  }

  // Validate URLs
  validItems.forEach(item => {
    if (!isValidUrl(item.url) && !item.url.startsWith('/')) {
      console.warn(`Invalid URL in breadcrumb item: ${item.name}`);
    }
  });

  // Create breadcrumb schema
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': validItems.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url
    })) as ListItem[]
  } as WithContext<BreadcrumbList>;
}

/**
 * Creates a breadcrumb schema with standard e-commerce hierarchy
 * @param options - Options for breadcrumb generation
 * @returns JSON-LD schema for breadcrumbs or null if validation fails
 */
export function generateEcommerceBreadcrumbSchema(
  options: EcommerceBreadcrumbOptions
): WithContext<BreadcrumbList> | null {
  const {
    baseURL,
    lang,
    currentPageName,
    currentPageURL,
    category,
    categoryURL,
    subcategory,
    subcategoryURL
  } = options;

  // Validate required fields
  if (!baseURL || !lang || !currentPageName || !currentPageURL) {
    console.warn('Missing required fields for e-commerce breadcrumb generation');
    return null;
  }

  // Create basic breadcrumb items
  const breadcrumbItems: BreadcrumbItem[] = [
    { name: 'Home', url: `${baseURL}/${lang}` },
    { name: 'Shop', url: `${baseURL}/${lang}/shop` }
  ];

  // Add category if available
  if (category && categoryURL) {
    breadcrumbItems.push({ name: category, url: categoryURL });
  } else if (category) {
    breadcrumbItems.push({ name: category, url: `${baseURL}/${lang}/shop/${category.toLowerCase()}` });
  }

  // Add subcategory if available
  if (subcategory && subcategoryURL) {
    breadcrumbItems.push({ name: subcategory, url: subcategoryURL });
  } else if (subcategory && category) {
    breadcrumbItems.push({
      name: subcategory,
      url: `${baseURL}/${lang}/shop/${category.toLowerCase()}/${subcategory.toLowerCase()}`
    });
  }

  // Add current page
  breadcrumbItems.push({ name: currentPageName, url: currentPageURL });

  // Generate breadcrumb schema
  return generateBreadcrumbSchema(breadcrumbItems);
}
